// Generic keynote-style renderers used by the airline-specific slide files.
// All content strings come from the airline content payload; no airline data
// is hard-coded here.
import { useState } from "react";
import { Network, Globe } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { resolveIcon } from "./iconMap";
import OverlayWrapper from "./OverlayWrapper";

/**
 * AirlineSlideHead — the shared eyebrow + headline used across airline slides.
 *  { eyebrow, title, accentLine, airline, accent }
 */
export const AirlineSlideHead = ({ eyebrow, title, accentLine, airline, accent, action }) => {
  const { isDark } = useTheme();
  return (
    <div className="flex items-start justify-between gap-6 mb-6">
      <div>
        <div className="k-eyebrow" style={{ color: accent }}>
          {eyebrow} · {airline.name}
        </div>
        <h2 className={`k-h2 mt-3 max-w-[16ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
          {title}
          {accentLine && <span className="block" style={{ color: accent }}>{accentLine}</span>}
        </h2>
      </div>
      {action}
    </div>
  );
};

/**
 * TeamFlatGrid — presented as a 5-col grid of member cards under the hero.
 * Data comes from airline.content.team.
 */
export const TeamFlatGrid = ({ airline, brand }) => {
  const { isDark } = useTheme();
  const [showOrganigram, setShowOrganigram] = useState(false);
  const t = airline?.content?.team || {};
  const keyTeam = t.keyTeam || [];
  const teamClusters = t.clusters || [];

  return (
    <div>
      <AirlineSlideHead
        eyebrow="Team"
        title="A dedicated team,"
        accentLine="in your time zone."
        airline={airline}
        accent={brand}
        action={
          <button
            onClick={() => setShowOrganigram(true)}
            data-testid="open-organigram-btn"
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-[13px] font-medium flex-shrink-0 ${
              isDark
                ? "bg-white/[0.06] text-white border border-white/15 hover:bg-white/[0.10]"
                : "bg-black/[0.04] text-[#1d1d1f] border border-black/15 hover:bg-black/[0.07]"
            }`}
          >
            <Network className="w-4 h-4" />
            View organigram
          </button>
        }
      />
      <p className={`k-lead max-w-[54ch] mb-2 ${isDark ? "text-white/70" : "text-black/60"}`}>
        {t.subheading}
      </p>
      {t.highlightBadge && (
        <p className="text-[15px] font-semibold" style={{ color: brand }}>{t.highlightBadge}</p>
      )}

      <div className={`mt-10 pt-6 border-t ${isDark ? "border-white/10" : "border-black/10"}`}>
        <div className="k-eyebrow" style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)" }}>
          Key people
        </div>
        <div className="mt-4 grid grid-cols-5 gap-3">
          {keyTeam.map((person, i) => (
            <div
              key={i}
              data-testid={`team-member-${i}`}
              className={`rounded-xl p-4 border transition-all ${
                isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base leading-none">{person.flag}</span>
                <div className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                  {person.name}
                </div>
              </div>
              <div className={`text-[12px] ${isDark ? "text-white/55" : "text-black/55"}`}>{person.role}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4">
        {teamClusters.map((cluster, i) => (
          <div key={i}>
            <div className="k-eyebrow" style={{ color: brand }}>{cluster.label}</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {cluster.tags.map((tag, j) => (
                <span
                  key={j}
                  className="text-[12px] px-2.5 py-1 rounded-full font-medium"
                  style={{
                    backgroundColor: isDark ? `${brand}25` : `${brand}18`,
                    color: isDark ? brand : "#1d1d1f",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {t.footNote && (
        <div className={`mt-10 pt-6 border-t flex items-center gap-2 text-[13px] italic ${
          isDark ? "border-white/10 text-white/45" : "border-black/10 text-black/45"
        }`}>
          <Globe className="w-3.5 h-3.5" />
          {t.footNote}
        </div>
      )}

      {showOrganigram && t.organigramUrl && (
        <OverlayWrapper
          isDark={isDark}
          onClose={() => setShowOrganigram(false)}
          icon={Network}
          iconColor={brand}
          title="Team Organigram"
          subtitle={`${airline.name} dedicated team structure`}
          widthClass="max-w-7xl"
        >
          <div style={{ background: "linear-gradient(to bottom, #0f172a, #020617)", padding: "3%" }}>
            <img src={t.organigramUrl} alt={`${airline.name} Team Organigram`} className="w-full h-auto object-contain block" data-testid="organigram-image" />
          </div>
        </OverlayWrapper>
      )}
    </div>
  );
};

/**
 * ServicesClusters — 3-column keynote services layout (SQ, RBA).
 */
export const ServicesClusters = ({ airline, brand }) => {
  const { isDark } = useTheme();
  const [activeIndex, setActiveIndex] = useState(null);
  const s = airline?.content?.services || {};
  const clusters = s.clusters || [];

  const resolveColor = (c) => (c === "brand" ? brand : c || brand);

  return (
    <div>
      <AirlineSlideHead
        eyebrow="Services"
        title="Every service"
        accentLine="in one contract."
        airline={airline}
        accent={brand}
      />
      <p className={`k-lead max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        {s.subheading}
      </p>

      <div className={`mt-12 pt-8 border-t grid grid-cols-3 gap-8 ${isDark ? "border-white/10" : "border-black/10"}`}>
        {clusters.map((cluster, i) => {
          const ClusterIcon = resolveIcon(cluster.iconName);
          const color = resolveColor(cluster.color);
          const isActive = activeIndex === i;
          return (
            <div key={i} data-testid={`service-cluster-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
                  <ClusterIcon className="w-4 h-4" style={{ color }} />
                </div>
                <div className={`text-[19px] font-semibold tracking-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                  {cluster.title}
                </div>
              </div>
              <ul className={`space-y-2.5 ${isDark ? "text-white/75" : "text-black/70"}`}>
                {(cluster.items || []).map((item, j) => {
                  const ItemIcon = resolveIcon(item.iconName);
                  return (
                    <li key={j} className="flex items-center gap-2.5 text-[14px]">
                      <ItemIcon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                      <span>{item.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};
